/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import moment from 'moment';
import * as Yup from 'yup';
import { Formik } from 'formik';

import { useAppDispatch } from 'hooks/redux';
import Button from '../../../atoms/Button';
import Toast from 'components/atoms/Toast';
import { useGetLabOfficesQuery } from 'store/services/office';
import { useLazyGetLabPatientListQuery, useLazyGetLabPatientQuery } from 'store/services/patient';
import { patientNewSaved } from 'store/slice/patientSlice';
import NewPatientForm, { FormValues } from 'components/lab/fax/createOrder/NewPatientForm';
import { PatientData } from '../types';
import { confirm } from 'components/molecules/Confirmation';
import { findDuplicatePatients } from 'helpers/duplicatePatient';
import { getDuplicatePatientConfirmationOptions } from 'helpers/duplicatePatientConfirmation';

const dobRegex = /(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.](19|20)\d\d/;

interface NewPatientProps {
  patientData: PatientData;
}

const patientValidation = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  dob: Yup.string()
    .required('Required')
    .test(
      'dob-check',
      'A valid date needs to be entered',
      (val) => dobRegex.test(val) && moment(val).isValid(),
    ),
  gender: Yup.string().required('Required'),
  officeId: Yup.string().required('Required'),
});

const NewPatient = ({ patientData }: NewPatientProps) => {

  const dispatch = useAppDispatch();
  const { data: offices } = useGetLabOfficesQuery({});
  const [getPatients] = useLazyGetLabPatientListQuery();
  const [getPatient] = useLazyGetLabPatientQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: FormValues = {
    firstName: patientData?.patientFirstName ||  '',
    middleName: '',
    lastName: patientData?.patientLastName || '',
    suffix: '',
    dob: patientData?.patientDob ? moment(patientData.patientDob).format('MM-DD-YYYY') : '',
    gender: patientData?.patientGender || '',
    officeId: '',
    mobilePhone: patientData?.patientPhone || '',
    primaryAddressCity: patientData?.patientAddressCity || '',
    primaryAddressState: patientData?.patientAddressState || '',
    primaryAddressZip: patientData?.patientAddressZip || '',
    primaryAddressStreetLine1: patientData?.patientAddressLine || '',
  };

  const onSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);

      let duplicates = [];

      try {
        const duplicateSearch = await getPatients({
          firstName: values.firstName,
          lastName: values.lastName,
          dob: moment(values.dob, 'MM/DD/YYYY').format('YYYY-MM-DD'),
          officeId: values.officeId,
          size: 100,
        }).unwrap();

        duplicates = findDuplicatePatients(values, duplicateSearch?.items);
      } catch {
        duplicates = [];
      }

      if (duplicates.length) {
        const firstDuplicate = duplicates[0];

        try {
          await confirm('', getDuplicatePatientConfirmationOptions());
        } catch {
          if (!firstDuplicate?.id) {
            return;
          }

          try {
            const existingPatient = await getPatient({ id: Number(firstDuplicate.id) }).unwrap();
            dispatch(patientNewSaved(existingPatient));
          } catch (error: any) {
            Toast({
              type: 'error',
              label: `Error loading existing patient: ${error?.data?.message || error?.message || 'Unknown error'}`,
            });
          }

          return;
        }
      }

      dispatch(patientNewSaved(values));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="card card-primary">
        <div className="card-body">
          <div className="row">
            <div className="col-sm-12">
              <Formik
                enableReinitialize
                validateOnBlur
                validationSchema={patientValidation}
                initialValues={initialValues}
                onSubmit={onSubmit}>
                {({
                    values,
                    errors,
                    touched,
                    dirty,
                    handleBlur,
                    isValid,
                    handleChange,
                    handleSubmit,
                    setFieldValue,
                  }) => {
                  return (
                    <form id="newPatient" className="px-2" onSubmit={handleSubmit}>
                      <NewPatientForm
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        values={values}
                        errors={errors}
                        touched={touched}
                        offices={offices}
                        submitButton={
                          <Button
                            className="btn btn-danger"
                            type="submit"
                            form="newPatient"
                            disabled={!dirty || !isValid || isSubmitting}
                            label="Create New Patient"
                          />
                        }
                      />
                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewPatient;
